import React, { useState, useEffect, useMemo } from 'react';
import { DisplayedProduct, SearchedProductName, ETIMClass, ETIMFeature, FilterMode, SortPriority, FilterSortParameters } from '../types/models';
import { ApiService } from '../services/api';
import ProductVariantCard from './ProductVariantCard';
import EditableFeatureBadge from './EditableFeatureBadge';
import { useAppContext } from '../context/AppContext'; // Импортируем useAppContext

interface ProductVariantsListProps {
  spn: SearchedProductName;
  onProductSelected: (updatedSpn: SearchedProductName) => void;
  onFeaturesSaved: (updatedSpn: SearchedProductName) => void; // Добавлена новая пропс-функция
  refreshTrigger?: number;
  selectedBrand?: string | null;
}




const ProductVariantsList: React.FC<ProductVariantsListProps> = ({
  spn,
  onProductSelected,
  onFeaturesSaved, // Добавлена новая пропс-функция
  refreshTrigger,
  selectedBrand
}) => {
  const { categoryFeatures, fetchCategoryFeatures } = useAppContext(); // Получаем из контекста

  console.log('ProductVariantsList rendered');
  console.log('spn.category?.id:', spn.category?.id);
  console.log('categoryFeatures:', categoryFeatures);
  console.log('availableFeatures being passed:', categoryFeatures[spn.category?.id || -1]);

  const [allProductVariants, setAllProductVariants] = useState<DisplayedProduct[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [isApplyingChanges, setIsApplyingChanges] = useState<boolean>(false);

  // Состояние для фильтров и сортировки
  const [currentFilterMode, setCurrentFilterMode] = useState<FilterMode>(
    spn.filter_sort_params?.filter_mode || FilterMode.ALL_FILTERS
  );
  const [currentSortPriority, setCurrentSortPriority] = useState<SortPriority>(
    spn.filter_sort_params?.sort_priority || SortPriority.BY_TEXT_SIMILARITY
  );

  // Обновляем состояние при изменении spn из пропсов
  useEffect(() => {
    const initialFilterMode = spn.filter_sort_params?.filter_mode || FilterMode.ALL_FILTERS;
    let initialSortPriority = spn.filter_sort_params?.sort_priority || SortPriority.BY_TEXT_SIMILARITY;

    setCurrentFilterMode(initialFilterMode);

    if (initialFilterMode !== FilterMode.ALL_FILTERS) {
      initialSortPriority = SortPriority.BY_TEXT_SIMILARITY;
    }
    setCurrentSortPriority(initialSortPriority);

  }, [spn.filter_sort_params]);

  // Эффект для автоматической установки сортировки при изменении режима фильтрации
  useEffect(() => {
    if (currentFilterMode !== FilterMode.ALL_FILTERS) {
      if (currentSortPriority !== SortPriority.BY_TEXT_SIMILARITY) {
        setCurrentSortPriority(SortPriority.BY_TEXT_SIMILARITY);
        // Устанавливаем hasPendingChanges, так как это изменение должно быть сохранено
        setHasPendingChanges(true);
      }
    }
  }, [currentFilterMode, currentSortPriority]);


  // Функция для обновления характеристик SPN и вызова API
  const updateSpnFeatures = async (updatedFeatures: ETIMFeature[], filterSortParams?: FilterSortParameters) => {
    console.log("Attempting to update features on backend for SPN:", spn.id, "with features:", updatedFeatures, "and params:", filterSortParams);
    // setIsApplyingChanges(true) управляется из handleApplyChanges
    try {
      // Преобразуем характеристики, убедившись, что VALUE является строкой
      const featuresToSend = updatedFeatures.map(feature => ({
        ...feature,
        VALUE: feature.VALUE === null || feature.VALUE === undefined ? "" : feature.VALUE,
      }));
      // Вызываем новый метод API для обновления характеристик
      const backendUpdatedSpn = await ApiService.updateSpnFeatures(spn.id, featuresToSend, filterSortParams);
      spn.etim_features = backendUpdatedSpn.etim_features; // Обновляем локальный объект spn
      spn.filter_sort_params = backendUpdatedSpn.filter_sort_params; // Обновляем параметры фильтрации и сортировки
      onFeaturesSaved(spn); // Родитель обновит spn и, возможно, refreshTrigger
      setHasPendingChanges(false); // Сбрасываем флаг здесь при успехе
      setError(null);
    } catch (err) { // Изменено имя переменной ошибки во избежание конфликта
      console.error("Failed to update features on backend:", err);
      setError("Не удалось сохранить изменения характеристик.");
      // При ошибке hasPendingChanges остается true, isApplyingChanges будет сброшен в finally вызывающей функции
      throw err; // Перебрасываем ошибку, чтобы handleApplyChanges мог ее поймать
    }
  };

  // Обработчик изменения характеристики в EditableFeatureBadge
  const handleFeatureChange = (index: number, featureCode: string | null, value: string | null) => {
    const currentFeatures = spn.etim_features || [];
    const updatedFeatures = [...currentFeatures];

    if (featureCode === null) {
      // Если код характеристики сброшен или удален
      updatedFeatures.splice(index, 1);
    } else {
      // Пользователь выбрал характеристику или изменил значение
      const availableFeature = categoryFeatures[spn.category?.id || -1]?.find(f => f.FEATURE_CODE === featureCode);

      const newFeature: ETIMFeature = {
        FEATURE_CODE: featureCode,
        VALUE: value,
        name: availableFeature?.name || '', // Берем имя из доступных характеристик
        values: availableFeature?.values || [], // Берем возможные значения
      };

      if (index < updatedFeatures.length) {
        // Обновляем существующую характеристику
        updatedFeatures[index] = newFeature;
      } else {
        // Добавляем новую характеристику (этот случай, возможно, не нужен, т.к. добавление происходит через handleAddFeature)
        // Но оставим для надежности, если логика добавления изменится
        updatedFeatures.push(newFeature);
      }
    }

    // Обновляем состояние SPN у родителя для отображения локальных изменений
    const updatedSpnForParent: SearchedProductName = {
      ...spn,
      etim_features: updatedFeatures,
    };
    onProductSelected(updatedSpnForParent);

    // Проверяем, были ли реальные изменения, чтобы установить флаг
    const originalFeaturesString = JSON.stringify(currentFeatures);
    const updatedFeaturesString = JSON.stringify(updatedFeatures);

    if (originalFeaturesString !== updatedFeaturesString) {
      setHasPendingChanges(true);
    }
    // Не вызываем updateSpnFeatures здесь
  };

  // Обработчик удаления характеристики
  const handleRemoveFeature = (index: number) => {
    const currentFeatures = spn.etim_features || [];
    const updatedFeatures = currentFeatures.filter((_, i) => i !== index);

    // Обновляем состояние SPN у родителя для отображения локальных изменений
    const updatedSpnForParent: SearchedProductName = {
      ...spn,
      etim_features: updatedFeatures,
    };
    onProductSelected(updatedSpnForParent);
    setHasPendingChanges(true); // Удаление всегда является изменением
    // Не вызываем updateSpnFeatures здесь
  };

  // Обработчик добавления новой характеристики
  const handleAddFeature = () => {
    const currentFeatures = spn.etim_features || [];
    // Добавляем пустую характеристику для выбора с временным уникальным ID.
    const newFeature: ETIMFeature & { tempId?: string } = {
      FEATURE_CODE: '',
      name: '',
      VALUE: null,
      values: [],
      tempId: `new-${Date.now()}-${Math.random()}`, // Временный уникальный ID
    };
    const updatedFeatures = [...currentFeatures, newFeature];

    // Обновляем состояние SPN у родителя. API вызов произойдет при выборе характеристики в новом бейджике.
    const updatedSpn: SearchedProductName = {
      ...spn,
      etim_features: updatedFeatures,
    };
    // Обновляем состояние SPN у родителя. API вызов произойдет при выборе характеристики в новом бейджике.
    onProductSelected(updatedSpn); // Возвращаем вызов onProductSelected для локального обновления
    // Не вызываем updateSpnFeatures здесь, т.к. нет полной характеристики для сохранения.
    // Сохранение произойдет при выборе кода характеристики в новом бейджике через handleFeatureChange.
  };

  const handleApplyChanges = async () => {
    // Убираем проверку !spn.etim_features, чтобы кнопка была активна,
    // если изменились только filterMode или sortPriority.
    // Передаем spn.etim_features || [] для случая, если etim_features еще не определены.
    if (isApplyingChanges) return; // Предотвращаем двойное нажатие, если уже в процессе
    setIsApplyingChanges(true);
    setError(null); // Сбрасываем предыдущие ошибки перед новой попыткой
    try {
      const filterSortParams: FilterSortParameters = {
        filter_mode: currentFilterMode,
        sort_priority: currentSortPriority,
      };
      await updateSpnFeatures(spn.etim_features || [], filterSortParams);
      // setHasPendingChanges(false) будет вызвано внутри updateSpnFeatures при успехе
    } catch (error) {
      // Ошибка уже обработана и установлена в setError в updateSpnFeatures
      // hasPendingChanges останется true
      console.error("Error applying changes:", error);
    } finally {
      setIsApplyingChanges(false);
    }
  };

  // --- useEffect для ЗАГРУЗКИ вариантов ---
  // Запускается при смене SPN, триггере обновления или завершении поиска
  useEffect(() => {
    setAllProductVariants([]); // Очищаем старые варианты

    if (!spn.is_searching) {
      console.log('Fetching ALL product variants for SPN:', spn.id, 'with brand:', selectedBrand);
      setIsLoading(true);
      setError(null);
      ApiService.getSPNProducts(spn.id, selectedBrand)
        .then(variants => {
          setAllProductVariants(variants);
          console.log('All variants loaded:', variants.length);
        })
        .catch(err => {
          console.error('Failed to fetch product variants:', err);
          setError('Не удалось загрузить варианты продуктов.');
          setAllProductVariants([]); // Очищаем при ошибке
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Если идет поиск, просто очищаем (уже сделано выше)
      // и не устанавливаем isLoading, т.к. показываем индикатор поиска
      setIsLoading(false);
    }
  }, [spn.id, refreshTrigger, spn.is_searching, selectedBrand]); // Теперь зависит от selectedBrand

  // --- useEffect для ЗАГРУЗКИ характеристик категории ---
  useEffect(() => {
    if (spn.category?.id) {
      fetchCategoryFeatures(spn.category.id);
    }
  }, [spn.category?.id, fetchCategoryFeatures, categoryFeatures]); // Добавляем categoryFeatures в зависимости useEffect

  // --- useMemo для ФИЛЬТРАЦИИ ---
  // Пересчитывается при изменении ВСЕХ вариантов или выбранного БРЕНДА
  const filteredProducts = useMemo<DisplayedProduct[]>(() => {
    console.log('Recalculating filtered products (brand filtering now on backend)');

    // Фильтрация по бренду теперь выполняется на бэкенде,
    // поэтому useMemo просто возвращает все загруженные варианты.
    return allProductVariants;
  }, [allProductVariants]); // Зависит только от allProductVariants

  const handleSelectProduct = async (productCode: string, product: DisplayedProduct) => {
    try {

      // Отправляем на бэкенд только код продукта
      const resp_spn = await ApiService.selectProduct(spn.id, productCode);
      // Обновляем локальный объект spn, сохраняя код продукта и его полные данные
      const updatedSpn: SearchedProductName = {
        ...resp_spn,
        orderNumber: spn.orderNumber,
        selected_product_details: product // Сохраняем полный объект продукта
      };
      console.log("Updated SPN:", updatedSpn); // Отладка
      onProductSelected(updatedSpn);
      onFeaturesSaved(updatedSpn);
    } catch (err) {
      console.error('Failed to select product:', err);
      setError('Не удалось выбрать продукт. Пожалуйста, попробуйте снова.');
    }
  };

  const handleDeselectProduct = async () => {
    try {
      // Отправляем запрос на отмену выбора
      await ApiService.deselectProduct(spn.id);

      // Обновляем локальный объект, очищая данные о продукте
      const updatedSpn: SearchedProductName = {
        ...spn,
        selected_product_code: null,
        selected_product_details: null,
        vendor_match: null
      };

      onProductSelected(updatedSpn);
      onFeaturesSaved(updatedSpn); // Вызываем onFeaturesSaved для обновления родителя
    } catch (err) {
      console.error('Failed to deselect product:', err);
      setError('Не удалось отменить выбор продукта. Пожалуйста, попробуйте снова.');
    }
  };

  // Если идет поиск, показываем индикатор загрузки
  if (spn.is_searching) {
    return (
      <div className="p-4 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg font-medium text-blue-600">Выполняется поиск продуктов...</p>
        <p className="text-sm text-gray-500 mt-2">Это может занять некоторое время</p>
      </div>
    );
  }

  // Если есть ошибка поиска
  if (spn.searchError) {
    return (
      <div className="p-4 text-center">
        <div className="bg-red-50 border border-red-200 p-3 rounded-md">
          <p className="text-red-600 font-medium">Произошла ошибка при поиске</p>
          <p className="text-sm text-red-500 mt-1">{spn.searchError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white shadow-sm p-4 flex flex-col" style={{ maxHeight: '80vh' }}>
      <h2 className="text-md font-semibold mb-4">Варианты продуктов для: "{spn.name}" {selectedBrand ? `(Бренд: ${selectedBrand})` : ''}</h2>
      <div className="overflow-y-auto pr-2 flex-grow">
        {/* Секция для редактирования характеристик SPN */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="product-variants-filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Переключатель режима поиска */}
              <div>
                <div className="flex items-center mb-1">
                  <label htmlFor="filterMode" className="block text-xs font-medium text-gray-700">Режим поиска:</label>
                  <span
                    className="ml-1.5 cursor-help text-blue-500 hover:text-blue-700"
                    title={
`1. Все фильтры: Поиск с учетом категории и всех указанных характеристик.
2. Без характеристик: Поиск только по категории, характеристики игнорируются.
3. Без фильтров: Глобальный поиск по всем продуктам, категория и характеристики игнорируются.
Для пп 2 и 3 применяется сортировка по схожести текста.`
                    }
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                  </span>
                </div>
                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mr-2 mb-2 bg-blue-50 text-blue-700 border border-blue-200">
                  <select
                    id="filterMode"
                    name="filterMode"
                    className="bg-transparent text-blue-700 focus:outline-none cursor-pointer truncate appearance-none w-auto pr-4"
                    value={currentFilterMode}
                    onChange={(e) => {
                      const newMode = e.target.value as FilterMode;
                      setCurrentFilterMode(newMode);
                      setHasPendingChanges(true);
                      if (newMode !== FilterMode.ALL_FILTERS) {
                        setCurrentSortPriority(SortPriority.BY_TEXT_SIMILARITY);
                      }
                    }}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.2em 1.2em',
                    }}
                  >
                    <option value={FilterMode.ALL_FILTERS}>Все фильтры</option>
                    <option value={FilterMode.NO_FEATURES}>Без характеристик</option>
                    <option value={FilterMode.NO_CATEGORY_NO_FEATURES}>Без фильтров</option>
                  </select>
                </div>
              </div>

              {/* Переключатель приоритета сортировки */}
              <div>
                <div className="flex items-center mb-1">
                  <label htmlFor="sortPriority" className="block text-xs font-medium text-gray-700">Приоритет сортировки:</label>
                  <span
                    className="ml-1.5 cursor-help text-blue-500 hover:text-blue-700"
                    title={
`По схожести текста: Сначала наиболее похожие по тексту, затем по количеству совпавших характеристик.
По примененным характеристикам: Сначала с наибольшим количеством совпавших характеристик, затем по схожести текста.`
                    }
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                  </span>
                </div>
                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mr-2 mb-2 bg-blue-50 text-blue-700 border border-blue-200">
                  <select
                    id="sortPriority"
                    name="sortPriority"
                    className={`bg-transparent text-blue-700 focus:outline-none cursor-pointer truncate appearance-none w-auto pr-4 ${currentFilterMode !== FilterMode.ALL_FILTERS ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    value={currentSortPriority}
                    disabled={currentFilterMode !== FilterMode.ALL_FILTERS}
                    onChange={(e) => {
                      setCurrentSortPriority(e.target.value as SortPriority);
                      setHasPendingChanges(true);
                    }}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.2em 1.2em',
                    }}
                  >
                    <option value={SortPriority.BY_TEXT_SIMILARITY}>По схожести текста</option>
                    <option value={SortPriority.BY_APPLIED_FEATURES}>По примененным характеристикам</option>
                  </select>
                </div>
              </div>
            </div>

            <h1 className="text-xs font-semibold text-blue-800 mb-3">
              Характеристики (для режима "Все фильтры")
            </h1>
            <div className="flex flex-wrap items-center">
              {/* Отображение существующих характеристик SPN */}
              {spn.etim_features && spn.etim_features.map((feature, index) => (
                <EditableFeatureBadge
                  key={feature.FEATURE_CODE || (feature as any).tempId || index} // Используем FEATURE_CODE, временный ID или индекс как ключ
                  availableFeatures={categoryFeatures[spn.category?.id || -1] || []} // Передаем доступные характеристики для категории
                  initialFeature={feature}
                  onFeatureChange={(featureCode, value) => handleFeatureChange(index, featureCode, value)}
                  onRemove={() => handleRemoveFeature(index)}
                  // isPending={feature.isPending} // Если есть флаг pending
                />
              ))}
              {/* Кнопка добавления новой характеристики */}
              <button
                className="ml-2 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300 hover:bg-green-200"
                onClick={handleAddFeature}
                disabled={currentFilterMode !== FilterMode.ALL_FILTERS} // Блокируем если не тот режим
                title={currentFilterMode !== FilterMode.ALL_FILTERS ? "Доступно только в режиме 'Все фильтры'" : "Добавить характеристику"}
              >
                + Добавить характеристику
              </button>
              {hasPendingChanges && (
                <button
                  onClick={handleApplyChanges}
                  disabled={isApplyingChanges}
                  className="ml-2 px-3 py-1.5 rounded-md text-sm font-medium bg-yellow-500 text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-50"
                >
                  {isApplyingChanges ? 'Применение...' : 'Применить изменения'}
                </button>
              )}
            </div>
            
             {/* Индикатор текущего режима */}
            {/* <div className="mt-3 text-xs text-gray-600">
              Текущий режим: <span className="font-semibold">
                {currentFilterMode === FilterMode.ALL_FILTERS && "Все фильтры"}
                {currentFilterMode === FilterMode.NO_FEATURES && "Без характеристик"}
                {currentFilterMode === FilterMode.NO_CATEGORY_NO_FEATURES && "Без фильтров"}
              </span>,
              Сортировка: <span className="font-semibold">
                {currentSortPriority === SortPriority.BY_TEXT_SIMILARITY && "По схожести текста"}
                {currentSortPriority === SortPriority.BY_APPLIED_FEATURES && "По примененным характеристикам"}
              </span>.
            </div> */}
          </div>
      
      {isLoading ? (
        <div className="flex justify-center p-6">
          <span className="loading">Загрузка продуктов...</span>
        </div>
      ) : error ? (
        <div className="text-red-500 p-4">{error}</div>
      ) : (
        <div >
          

          {/* Отображение отфильтрованных продуктов */}
          <div className="space-y-1 mt-2">
            {filteredProducts.map((product) => (
              <ProductVariantCard
                key={product.code}
                product={product}
                isSelected={spn.selected_product_code === product.code}
                onSelect={handleSelectProduct}
                onDeselect={handleDeselectProduct}
                spn={spn} // Передаем spn как проп
              />
            ))}
          </div>

          {filteredProducts.length === 0 && !isLoading && (
            <div className="text-gray-500 text-center py-8">
              {selectedBrand
                ? `Нет продуктов бренда "${selectedBrand}", соответствующих критериям.`
                : 'Нет доступных вариантов продуктов.'}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default ProductVariantsList;
